// @flow

import * as React from 'react';

import {
  selectCartIsActive,
  selectCartCurrency,
  type ProductId,
} from '@gandi/cart';
import { Select } from '@gandi/form';
import { useTranslator } from '@gandi/translate';

import type { TranslatorFunction } from '@gandi/translate';

import type { GandiFormSelectOption } from 'src/Domain/types';
import { useAppSelector } from 'src/hooks/useAppSelector';

import { useBestCatalogPrice } from '../../../modules/catalog';

const getDurationLabel = (
  optionValue: number,
  unit: string,
  hasMonthly: boolean,
  hasYearly: boolean,
  hasMultiyear: boolean,
  __: TranslatorFunction,
): string => {
  const count = String(optionValue);
  const hasSpecialMonthlyLabels = unit === 'm' && hasMonthly && hasYearly;

  if (!hasSpecialMonthlyLabels) {
    return unit === 'y'
      ? __('%(count)s years', { count })
      : __('%(count)s months', { count });
  }

  if (optionValue === 1) {
    return __('Monthly');
  }

  if (optionValue === 12 && !hasMultiyear) {
    return __('Yearly');
  }

  if (optionValue % 12 === 0) {
    const yearsCount = String(optionValue / 12);
    return `${__('Yearly')} – ${__('%(count)s years', { count: yearsCount })}`;
  }

  return __('%(count)s months', { count });
};

export type Props = {
  disabled: boolean,
  onChange?: (value: GandiFormSelectOption<number>) => mixed,
  value?: number,
  id: string,
  unit: 'm' | 'y',
  values: Array<number>,
  readOnly?: boolean,
  productId: ProductId,
};

export default function DurationSelect({
  disabled,
  onChange,
  value,
  id,
  unit,
  values,
  readOnly,
  productId,
}: Props): React.MixedElement {
  const { __, formatPrice } = useTranslator();
  const cartActive = useAppSelector(selectCartIsActive);

  const currency = useAppSelector(selectCartCurrency);

  const { getBestPrice } = useBestCatalogPrice(productId);

  const hasMonthly = values.includes(1);
  const hasYearly = values.some(v => v >= 12);
  const hasMultiyear = values.some(v => v > 12);

  const options: Array<GandiFormSelectOption<number>> = values.map(
    optionValue => {
      const label = getDurationLabel(
        optionValue,
        unit,
        hasMonthly,
        hasYearly,
        hasMultiyear,
        __,
      );

      const correspondingPrice = getBestPrice(optionValue, unit);
      if (correspondingPrice) {
        const price = formatPrice(
          correspondingPrice.price_before_taxes * optionValue,
          currency,
        );

        return {
          text: `${label} – ${price}`,
          value: optionValue,
        };
      }

      return {
        text: label,
        value: optionValue,
      };
    },
  );

  const selectedOption = options.find(opt => opt.value === value)?.value;

  const isDisabled = disabled || options.length === 0 || !cartActive;

  const onSelectChange: (SyntheticEvent<HTMLSelectElement>) => void = evt => {
    const newDuration = options.find(
      opt => opt.value === parseInt(evt.currentTarget.value, 10),
    );
    if (onChange && newDuration !== undefined) {
      onChange(newDuration);
    }
  };

  return (
    <Select
      disabled={isDisabled}
      id={id}
      onChange={e => onSelectChange(e)}
      value={selectedOption}
      values={options}
      readOnly={readOnly || (!disabled && options.length === 1)}
      variant={['small']}
      aria-label={__('Duration')}
    />
  );
}
